import {CaretDownOutlined} from '@ant-design/icons';
import {Checkbox, Form, Input, Select} from 'antd';
import {Option} from 'antd/es/mentions';
import {
  OnlyAlphabetInputValidatorWithSpecialCharacters,
  isInPersonCapacityFull,
  occupiesRepSlot,
  uniqueSponsorPerksValueEnum,
  validateCompanyName,
} from '../../../Util/commonUtility';
import {useState} from 'react';
import useLocalization from '../../../Localization';
import ViewportSelect from '../ViewportPopup/ViewportSelect';

const SponsorEditableCell = ({
  editing,
  dataIndex,
  title,
  eventType,
  record,
  index,
  children,
  allEventOptionsFoodData,
  attendeesData = [],
  limitAttendeesData,
  handleAttendeePresentationChange,
  checkedAttendeePresentation,
  handleAttendeeDinnerChange,
  checkedAttendeeDinner,
  SponsorPerks,
  sponsorOption,
  transactionId,
  eventISOnline,
  inPersonRemaining,
  ...restProps
}) => {
  const {ApplicationString} = useLocalization();
  let inputElement;
  // Same capacity rules as stage: disable only when limit is full.
  // Row that already has Yes stays enabled so it can be cleared.
  // No red "full" text — disabled checkbox is enough.
  const AttendeePresentationLimit = SponsorPerks?.workshop_attendees;
  const AttendeeDinnerLimit = SponsorPerks?.dinner_with_speaker;
  // Perks are shared by every rep of the sponsorship, including reps paid in other payments.
  // Refunded / refund-pending rows no longer occupy slots (same as API occupiesRepSlot).
  const perkAttendeesData = (limitAttendeesData || attendeesData)?.filter(
    (obj) => occupiesRepSlot(obj?.status),
  );
  const allAttendeePresentationData = perkAttendeesData?.filter(
    (obj) => obj.attending_presentation,
  );
  const allAttendeeDinnerData = perkAttendeesData?.filter(
    (obj) => obj.attending_dinner,
  );
  const disablePresentationCheckboxValue =
    allAttendeePresentationData?.length >= AttendeePresentationLimit;
  const inPersonCapacityFull = isInPersonCapacityFull(inPersonRemaining);
  const disablePresentationCheckbox = record?.attending_presentation
    ? false
    : disablePresentationCheckboxValue || inPersonCapacityFull;
  const disableDinnerCheckboxValue =
    allAttendeeDinnerData?.length >= AttendeeDinnerLimit;
  const disableDinnerCheckbox = record?.attending_dinner
    ? false
    : disableDinnerCheckboxValue;

  const lunchLimit = SponsorPerks?.free_lunch;

  const disableLunchOptions = (foodOption) => {
    let disabledValue;
    const totalRemainingQuantity = foodOption?.qty - foodOption?.used_qty;
    const checkIsSponsorOption =
      typeof foodOption?.value === 'string' &&
      foodOption?.value?.includes(uniqueSponsorPerksValueEnum);

    // Free lunch: priced at the sponsor lunch price, or bought below list price (lunch price edited later).
    const allAttendeeFreeFilterData = perkAttendeesData?.filter((obj) => {
      const food = obj?.event_registration_options?.food;
      if (!food) return false;
      if (
        food.list_price !== undefined &&
        food.list_price !== null &&
        Number(food.price) < Number(food.list_price)
      ) {
        return true;
      }
      return food.price === foodOption?.discounted_price;
    });
    const allAttendeeFilterData = attendeesData?.filter(
      (obj) =>
        obj?.event_registration_options?.food?.event_option_id ===
        foodOption.id,
    );
    if (checkIsSponsorOption) {
      disabledValue =
        totalRemainingQuantity === 0 ||
        allAttendeeFreeFilterData?.length >= lunchLimit ||
        allAttendeeFilterData?.length >= totalRemainingQuantity;
    } else {
      disabledValue =
        totalRemainingQuantity === 0 ||
        allAttendeeFilterData?.length >= totalRemainingQuantity;
    }
    return disabledValue;
  };

  const uniqueEmailValidator = (value, record) => {
    if (record?.attendee_email !== value && attendeesData?.length > 0) {
      const sameEmail = attendeesData?.find(
        (obj) => obj?.attendee_email === value,
      );
      if (sameEmail) {
        return Promise.reject(
          new Error(
            `${ApplicationString['sponsorEditableCell.emailAlreadyExists'](
              value,
            )}`,
          ),
        );
      }
    }
    return Promise.resolve();
  };

  const [isActive, setActive] = useState(true);
  const toggleClass = () => {
    setActive(!isActive);
  };

  switch (dataIndex) {
    case 'name':
      inputElement = (
        <div className='field-separation'>
          <Form.Item
            name='attendee_first_name'
            className='no-margin'
            rules={[
              {
                required: true,
                message:
                  ApplicationString[
                    'sponsorEditableCell.pleaseFillOutFirstName'
                  ],
              },
              {
                validator: (_, value) =>
                  OnlyAlphabetInputValidatorWithSpecialCharacters(
                    value,
                    'First name',
                  ),
              },
            ]}>
            <Input
              id='attendee_first_name'
              placeholder={
                ApplicationString['sponsorEditableCell.enterAttendeeFirstName']
              }
            />
          </Form.Item>
          <Form.Item
            name='attendee_last_name'
            className='no-margin'
            rules={[
              {
                required: true,
                message:
                  ApplicationString[
                    'sponsorEditableCell.pleaseFillOutLastName'
                  ],
              },
              {
                validator: (_, value) =>
                  OnlyAlphabetInputValidatorWithSpecialCharacters(
                    value,
                    'Last name',
                  ),
              },
            ]}>
            <Input
              id='attendee_last_name'
              placeholder={
                ApplicationString['sponsorEditableCell.enterAttendeeLastName']
              }
            />
          </Form.Item>
        </div>
      );
      break;
    case 'attendee_email':
      inputElement = (
        <div className='field-separation'>
          <Form.Item
            name={dataIndex}
            className='no-margin'
            rules={[
              {
                required: true,
                message: ApplicationString['sponsorEditableCell.pleaseFillOut'](
                  title?.toLowerCase(),
                ),
              },
              {
                type: 'email',
                message:
                  ApplicationString[
                    'sponsorEditableCell.pleaseEnterValidEmailAddress'
                  ],
              },
              {
                validator: (_, value) => uniqueEmailValidator(value, record),
              },
            ]}>
            <Input
              id={dataIndex}
              onCopy={(e) => e.preventDefault()} // Disable copy
              onPaste={(e) => e.preventDefault()} // Disable paste
              placeholder={`${ApplicationString[
                'sponsorEditableCell.enterAttendee'
              ](title?.toLowerCase())}`}
            />
          </Form.Item>
          <Form.Item
            name={`${dataIndex}_confirm`}
            className='no-margin'
            rules={[
              {
                required: true,
                message: ApplicationString[
                  'sponsorEditableCell.pleaseConfirmAttendee'
                ](title?.toLowerCase()),
              },
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (
                    !value ||
                    getFieldValue(dataIndex)?.toLowerCase() ===
                      value?.toLowerCase()
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      ApplicationString['editableCell.emailIdsMustMatch'],
                    ),
                  );
                },
              }),
            ]}>
            <Input
              id={`${dataIndex}_confirm`}
              onCopy={(e) => e.preventDefault()} // Disable copy
              onPaste={(e) => e.preventDefault()} // Disable paste
              placeholder={`${ApplicationString[
                'sponsorEditableCell.confirmAttendee'
              ](title?.toLowerCase())}`}
            />
          </Form.Item>
        </div>
      );
      break;
    case 'attendee_company':
      inputElement = (
        <Form.Item
          name={dataIndex}
          rules={[
            {
              required: true,
              message: ApplicationString['sponsorEditableCell.pleaseFillOut'](
                title?.toLowerCase(),
              ),
            },
            {
              validator: (_, value) => validateCompanyName(value, title),
            },
          ]}
          style={{margin: 0}}>
          <Input
            id={dataIndex}
            placeholder={`${ApplicationString[
              'sponsorEditableCell.enterAttendee'
            ](title?.toLowerCase())}`}
          />
        </Form.Item>
      );
      break;
    case 'option':
      inputElement = (
        <div className='field-separation'>
          {(inputElement = eventType !== 'Webcast' || !eventISOnline) && (
            <Form.Item
              name='food_option'
              className='no-margin'
              rules={[
                {
                  required: true,
                  message:
                    ApplicationString[
                      'sponsorEditableCell.pleaseSelectAttendeeLunchOption'
                    ],
                },
              ]}>
              <ViewportSelect
                id='food_option'
                showSearch
                allowClear
                optionFilterProp='label'
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(String(input ?? '').toLowerCase())
                }
                disabled={Boolean(transactionId)}
                placeholder={
                  ApplicationString[
                    'sponsorEditableCell.selectAttendeeLunchOption'
                  ]
                }
                className='editable-options'
                suffixIcon={
                  <CaretDownOutlined
                    className={isActive ? 'ant-select-suffix' : ''}
                    onClick={toggleClass}
                  />
                }>
                {allEventOptionsFoodData?.map((foodOption) => (
                  <Option
                    key={foodOption.label}
                    value={foodOption.value}
                    label={foodOption.label}
                    disabled={disableLunchOptions(foodOption)}>
                    {foodOption.label}
                  </Option>
                ))}
              </ViewportSelect>
            </Form.Item>
          )}
        </div>
      );
      break;
    case 'attending_presentation':
      inputElement = (
        <Form.Item name={dataIndex} style={{margin: 0}}>
          <div className='checkbox-editableCell'>
            <Checkbox
              onChange={handleAttendeePresentationChange}
              checked={checkedAttendeePresentation}
              disabled={
                Boolean(transactionId) || disablePresentationCheckbox
              }></Checkbox>
          </div>
        </Form.Item>
      );
      break;
    case 'attending_dinner':
      inputElement = (
        <Form.Item name={dataIndex} style={{margin: 0}}>
          <div className='checkbox-editableCell'>
            <Checkbox
              onChange={handleAttendeeDinnerChange}
              checked={checkedAttendeeDinner}
              disabled={
                Boolean(transactionId) || disableDinnerCheckbox
              }></Checkbox>
          </div>
        </Form.Item>
      );
      break;
    default:
      inputElement = (
        <Form.Item
          name={dataIndex}
          rules={[
            {
              required: true,
              message: ApplicationString['sponsorEditableCell.pleaseFillOut'](
                title?.toLowerCase(),
              ),
            },
            {
              validator: (_, value) =>
                OnlyAlphabetInputValidatorWithSpecialCharacters(value, title),
            },
          ]}
          style={{margin: 0}}>
          <Input
            placeholder={`${ApplicationString[
              'sponsorEditableCell.enterAttendee'
            ](title?.toLowerCase())}`}
          />
        </Form.Item>
      );
  }

  return (
    <td {...restProps}>{editing ? <div>{inputElement}</div> : children}</td>
  );
};
export default SponsorEditableCell;
